/**
 * Firebase Configuration
 * Handles Firebase initialization and authentication
 */

import { initializeApp } from 'firebase/app';
import {
	getAuth,
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signInWithPopup,
	signInWithRedirect,
	getRedirectResult,
	GoogleAuthProvider,
	signOut,
	onAuthStateChanged,
	sendPasswordResetEmail,
	User
} from 'firebase/auth';

// Firebase configuration
// These values should be in environment variables in production
const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDemoKey",
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "clawdgate.firebaseapp.com",
	projectId: "clawdgate",
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "clawdgate.appspot.com",
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
	appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abc123"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
	prompt: 'select_account'
});

// Apple Sign-In: Web uses a manual OAuth flow with the Services ID (com.clawdgate.id)
// because Firebase Console's Services ID is set to com.clawdgate (for native iOS).
// The redirect goes to the cloud API which extracts the token and sends it back.
const APPLE_CLIENT_ID = 'com.clawdgate.id';
const APPLE_REDIRECT_URI = (import.meta.env.VITE_API_URL || 'https://api.clawdgate.com') + '/api/v1/auth/apple-callback';

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email: string, password: string) => {
	try {
		const userCredential = await createUserWithEmailAndPassword(auth, email, password);
		return userCredential.user;
	} catch (error: any) {
		throw new Error(error.message);
	}
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email: string, password: string) => {
	try {
		const userCredential = await signInWithEmailAndPassword(auth, email, password);
		return userCredential.user;
	} catch (error: any) {
		throw new Error(error.message);
	}
};

/**
 * Sign in with Google
 * Tries popup first (more reliable on mobile browsers where redirect
 * fails due to ITP / third-party cookie restrictions). Falls back to
 * redirect when the popup is blocked (e.g. desktop ad-blockers).
 */
export const signInWithGoogle = async (): Promise<User | null> => {
	try {
		const result = await signInWithPopup(auth, googleProvider);
		return result.user;
	} catch (error: any) {
		// auth/popup-blocked or auth/popup-closed-by-user → fall back to redirect
		if (
			error.code === 'auth/popup-blocked' ||
			error.code === 'auth/cancelled-popup-request'
		) {
			console.log('Popup blocked, falling back to redirect');
			await signInWithRedirect(auth, googleProvider);
			return null;
		}
		// User closed the popup themselves — not an error, just cancelled
		if (error.code === 'auth/popup-closed-by-user') {
			return null;
		}
		throw error;
	}
};

/**
 * Generate a cryptographically secure random string.
 */
function generateNonce(length = 32): string {
	const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const values = crypto.getRandomValues(new Uint8Array(length));
	return Array.from(values, (v) => charset[v % charset.length]).join('');
}

/**
 * SHA-256 hash a string and return hex.
 */
async function sha256hex(plain: string): Promise<string> {
	const data = new TextEncoder().encode(plain);
	const hash = await crypto.subtle.digest('SHA-256', data);
	return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Start Apple Sign-In flow.
 * Redirects to Apple's auth page with Services ID (com.clawdgate.id).
 * Apple will POST back to /apple-callback with id_token and code.
 */
export const signInWithApple = async (): Promise<User | null> => {
	const nonce = generateNonce();
	const state = generateNonce(16);
	const hashedNonce = await sha256hex(nonce);

	// Store nonce and state for verification after redirect
	sessionStorage.setItem('apple_signin_nonce', nonce);
	sessionStorage.setItem('apple_signin_state', state);

	const params = new URLSearchParams({
		client_id: APPLE_CLIENT_ID,
		redirect_uri: APPLE_REDIRECT_URI,
		response_type: 'code id_token',
		response_mode: 'form_post',
		scope: 'email name',
		nonce: hashedNonce,
		state,
	});

	window.location.href = `https://appleid.apple.com/auth/authorize?${params.toString()}`;
	return null;
};

/**
 * Complete Apple Sign-In after redirect.
 * Verifies state, decodes the Apple ID token, and returns the user info
 * for direct cloud API sync (bypasses Firebase auth for web Apple sign-in
 * since Firebase's audience check rejects the Services ID token).
 */
export const completeAppleSignIn = async (idToken: string, state: string): Promise<{ sub: string; email: string; name?: string; idToken: string }> => {
	const savedState = sessionStorage.getItem('apple_signin_state');

	// Clean up
	sessionStorage.removeItem('apple_signin_state');
	sessionStorage.removeItem('apple_signin_nonce');

	if (!savedState || state !== savedState) {
		throw new Error('State mismatch — possible CSRF attack');
	}

	// Decode the Apple JWT to extract user info
	const parts = idToken.split('.');
	if (parts.length !== 3) {
		throw new Error('Invalid Apple ID token');
	}

	const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

	if (!payload.sub || !payload.email) {
		throw new Error('Apple token missing required claims');
	}

	return {
		sub: payload.sub,
		email: payload.email,
		name: payload.name,
		idToken,
	};
};

/**
 * Handle Google redirect result (call on app init)
 * Only needed for the redirect fallback path.
 */
export const handleGoogleRedirect = async (): Promise<User | null> => {
	try {
		const result = await getRedirectResult(auth);
		return result?.user ?? null;
	} catch (error: any) {
		console.error('Google redirect error:', error.message);
		return null;
	}
};

/**
 * Send password reset email via Firebase
 */
export const resetPassword = async (email: string) => {
	try {
		await sendPasswordResetEmail(auth, email);
	} catch (error: any) {
		throw new Error(error.message);
	}
};

/**
 * Sign out
 */
export const logOut = async () => {
	try {
		await signOut(auth);
	} catch (error: any) {
		throw new Error(error.message);
	}
};

/**
 * Auth state observer
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
	return onAuthStateChanged(auth, callback);
};

export default app;
